import { Popover } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { ListarResultadosExamenes } from "../../Service/Service.resultadosexamenes";
import ModalResultadoExamen from "./ModalResultadoExamen";
import { tonoEstadoResultado } from "./estadoResultadoTono";
import usePermissions from "../../hook/usePermissions";
import DataTable from "../UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../UI/Chip";
import Boton from "../UI/Boton";
import { IconoEditar, IconoMas, IconoInfo } from "../UI/Iconos";

// SISTEMA-DE-DISENO · pantalla migrada al kit (2ª, tras Auditoría Clínica).
//
// MIGRAR NO ES REDISEÑAR: la consulta, los filtros, los permisos, el modal y la
// identidad de fila (`result_key`) son EXACTAMENTE los de antes. Se mantienen
// también los comentarios que explican por qué cada dato es el que es -- son
// decisiones de negocio, no de estilo. Lo que cambia es cómo se ve:
//
//   · 8 columnas -> 7: paciente y su documento se leen juntos, en una celda.
//   · Estado y cobertura pasan a los chips del sistema, con el color
//     significando lo mismo que en el resto de la app.
//   · La tabla gana sus tres estados: cargando con esqueleto, vacío que
//     distingue "sin resultados" de "sin datos", y ERROR -- que antes no
//     existía: si la consulta fallaba, la pantalla se quedaba en blanco sin
//     decir nada.
//   · La acción se vuelve un botón del kit, con el mismo permiso dual de antes.
function GridResultadoExamen({ data }) {
    // RBAC-CUTOVER-05: la accion de resultados es dual (crea si el registro no
    // tiene resultado aun, edita si ya existe -- mismo patron que
    // ModalResultadoExamen.js) -- depende de resultados.create/edit real.
    const { hasPermission } = usePermissions();

    const { filters, onTotal } = data;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [resulExamenList, setResulExamenList] = useState([]);
    const [showUploadResultado, setShowUploadResultado] = useState(false);
    const [dataInvoice, setDataInvoice] = useState([]);

    // RESULTADOS-06G: cierre EXPLICITO (no toggle) para que ninguna re-render del
    // padre ni closure obsoleto pueda cerrar/abrir el modal por accidente. Las
    // acciones documentales del modal nunca llaman aca; solo Guardar (reload=true)
    // y Cancelar/X (reload=false).
    const handleCloseUpload = (reload = false) => {
        setShowUploadResultado(false);
        if (reload) {
            fetchInitialData(currentPage, pageSize, filters);
        }
    };

    // RESULTADOS-19: fecha CLINICA de la prestacion (backend `record.fecha`;
    // fallback `fecha_emision`). Es la fecha de la atencion, NO la de la
    // factura -- consistente para filas facturadas y `sin_factura`.
    const formatFecha = (record) => {
        const raw = record?.fecha || record?.fecha_emision;
        if (!raw) return null;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleDateString("es-EC", { year: "numeric", month: "2-digit", day: "2-digit" });
    };

    const columnas = [
        {
            // RESULTADOS-19: la columna Fecha va PRIMERA. Ordena el backend
            // (fecha DESC, mas reciente arriba) sobre el universo completo.
            key: "fecha",
            titulo: "Fecha",
            ancho: 110,
            render: (record) => <Celda valor={formatFecha(record)} />,
        },
        {
            // RESULTADOS-PACIENTE-SEGURO-FIX-14: el paciente lo resuelve el
            // backend desde la ATENCION CLINICA (prestacion facturada ->
            // procedimiento_cita_paciente), NO desde el pagador de la factura.
            // No depende de que exista factura al paciente (SEGURO100 / copago
            // con parte aseguradora pendiente). Si el backend no pudo
            // reconstruir la relacion clinica (historico), envia null y aqui se
            // muestra "Sin determinar" -- nunca en blanco, nunca inventado.
            //
            // El documento se lee junto al nombre: una celda, dos datos.
            // `tituloDeNombre` es SOLO presentacion: el valor original es el que
            // se sigue enviando, buscando y exportando.
            key: "paciente",
            titulo: "Paciente",
            render: (record) => (
                <CeldaDoble
                    principal={record.paciente ? tituloDeNombre(record.paciente) : "Sin determinar"}
                    secundario={record.numdoc}
                    monoSecundario
                />
            ),
        },
        {
            // RESULTADOS-03B: antes mostraba `categoria` (LABORATORIO/IMAGENES
            // generico para TODAS las filas de esa categoria). Ahora backend
            // (RESULTADOS-03A) ya resuelve `procedimiento` correctamente:
            // "LABORATORIO" para la fila LAB consolidada, y el nombre REAL del
            // examen para cada fila INDIVIDUAL (ej. "Ecografia Transvaginal").
            key: "procedimiento",
            titulo: "Procedimiento",
            render: (record) => {
                if (record.result_type !== "LAB") return <Celda valor={record.procedimiento} />;
                // LAB: seccion E -- no crear N filas, mostrar "LABORATORIO" +
                // cantidad, con un popover para inspeccionar los examenes
                // individuales incluidos.
                const examenes = record.examenes || [];
                const contenidoPopover = (
                    <ul className="ps-3 mb-0">
                        {examenes.map((ex) => (
                            <li key={ex.id}>{ex.nombre}</li>
                        ))}
                    </ul>
                );
                return (
                    <Popover content={contenidoPopover} title="Exámenes incluidos" trigger="click">
                        <span
                            style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}
                            data-testid={`resultados-lab-${record.result_key}`}
                        >
                            {record.procedimiento}
                            <ChipCategoria>{examenes.length} exámenes</ChipCategoria>
                            <IconoInfo size={15} />
                        </span>
                    </Popover>
                );
            },
        },
        {
            // RESULTADOS-07: cobertura HISTORICA de la factura (facturas.seguro_id
            // al momento de facturar, NO el seguro actual del paciente). El
            // backend ya la entrega normalizada en `record.coverage`.
            //
            // RESULTADOS-07-QA-FIX: la semantica "Particular" la decide el
            // backend. El frontend NUNCA la inventa: si `coverage` no viene
            // (regresion), lo marca como dato incompleto -- en ambar y con
            // icono -- en vez de disfrazarlo de "Particular".
            key: "cobertura",
            titulo: "Cobertura",
            ancho: 170,
            render: (record) =>
                record?.coverage?.name ? (
                    <ChipCategoria tono="info">{record.coverage.name}</ChipCategoria>
                ) : (
                    <ChipCategoria incompleto title="El backend no envió la cobertura de esta fila">
                        Cobertura no disponible
                    </ChipCategoria>
                ),
        },
        {
            key: "estado",
            titulo: "Estado",
            ancho: 170,
            render: (record) => {
                // RESULTADOS-03B seccion F/K: cada fila (LAB consolidada o
                // INDIVIDUAL) trae su PROPIO `resultado` (singular, ya no
                // `resultados[]`) -- el estado de una fila nunca afecta a otra.
                const estadoNombre = record.resultado?.estados?.nombre ?? "Sin estado";
                return (
                    <span style={{ display: "inline-flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                        <ChipEstado tono={tonoEstadoResultado(estadoNombre)}>{estadoNombre}</ChipEstado>
                        {record.resultado_es_legacy && (
                            <ChipCategoria incompleto title="Registrado antes de esta actualización -- ver seccion P">
                                Resultado histórico
                            </ChipCategoria>
                        )}
                    </span>
                );
            },
        },
        {
            key: "valor",
            titulo: "Total",
            ancho: 100,
            alinear: "derecha",
            render: (record) => <Celda valor={Number(record.valor).toFixed(2)} mono />,
        },
        {
            // Una sola columna de accion, sin cabecera, a la derecha.
            key: "accion",
            titulo: "",
            ancho: 80,
            alinear: "derecha",
            render: (record) => {
                // RESULTADOS-VER-SOLO-LECTURA: quien tiene `resultados.view`
                // puede ABRIR el resultado y ver/descargar sus documentos --
                // el backend siempre lo permitio (listar y descargar exigen
                // `resultados.view`), pero esta accion era la unica entrada al
                // modal y se escondia sin create/edit. El rol Doctor se
                // quedaba sin poder ver los resultados de sus pacientes.
                //
                // Lo que NO puede hacer sin create/edit lo sigue impidiendo el
                // modal: subir, reemplazar y borrar documentos, y Guardar.
                const puedeVer = hasPermission("resultados.view");
                const puedeSubir = hasPermission(record.resultado ? "resultados.edit" : "resultados.create");
                if (!puedeVer && !puedeSubir) return null;
                // Sin un resultado creado todavia no hay nada que ver: a quien
                // solo puede mirar no se le ofrece un boton que no hace nada.
                if (!record.resultado && !puedeSubir) return null;
                const soloVer = record.resultado && !puedeSubir;
                return (
                    <Boton
                        tamano="sm"
                        soloIcono
                        icono={record.resultado ? IconoEditar : IconoMas}
                        titulo={
                            soloVer
                                ? "Ver resultado"
                                : record.resultado ? "Ver / editar resultado" : "Nuevo resultado"
                        }
                        onClick={(e) => {
                            e.stopPropagation();
                            handleModalUploadDocument(record);
                        }}
                        data-testid={`resultados-accion-${record.result_key}`}
                    />
                );
            },
        },
    ];

    const handleModalUploadDocument = (datos) => {
        setDataInvoice(datos);
        setShowUploadResultado(true);
    };

    // RESULTADOS-04 seccion 13: evitar carreras entre requests (Buscar /
    // cambio de pagina / Limpiar rapidos) -- solo el ultimo request emitido
    // puede escribir en el estado.
    const requestIdRef = useRef(0);

    const fetchInitialData = useCallback(
        async (page = 1, limit = 10, filtros) => {
            const requestId = ++requestIdRef.current;
            setLoading(true);
            setError(null);
            const result = await ListarResultadosExamenes({ page, limit, filters: filtros });

            if (requestId !== requestIdRef.current) return;

            if (result && result.success) {
                // RESULTADOS-03B: backend (03A) ya no devuelve un `id` generico por
                // fila (antes era, sin querer, el iddfactura del ultimo detalle
                // recorrido -- ver RESULTADOS-FLUJO-01-AUDIT). La identidad real y
                // estable de cada fila es `result_key` (LAB:<factura_id> o
                // IMG:<factura_detalle_id>), provista integramente por backend --
                // el frontend NUNCA la reconstruye.
                const fetchInvoice = result.data.map((item, index) => ({
                    ...item,
                    key: item.result_key,
                    index,
                }));
                setResulExamenList(fetchInvoice);

                setTotal(result.pagination.total);
                setCurrentPage(result.pagination.page);
                setPageSize(result.pagination.limit);
                if (onTotal) onTotal(result.pagination.total);
            } else {
                // ESTADO DE ERROR: antes este caso no existía y la pantalla se
                // quedaba en blanco, indistinguible de "no hay resultados".
                setResulExamenList([]);
                setTotal(0);
                setError(result?.message || "No se pudo obtener el listado de resultados.");
            }

            setLoading(false);
        },
        [onTotal]
    );

    const pageSizeRef = useRef(pageSize);
    useEffect(() => {
        pageSizeRef.current = pageSize;
    }, [pageSize]);

    // Al cambiar filters se resetea explicitamente a la pagina 1 (no se reutiliza
    // currentPage, que puede apuntar a una pagina que ya no existe en el resultado
    // filtrado). pageSize se lee via ref para preservar el tamaño de pagina elegido
    // por el usuario sin que sus cambios disparen este efecto (evita reset de pagina
    // al solo cambiar el pageSize, sin cambiar filtros).
    useEffect(() => {
        fetchInitialData(1, pageSizeRef.current, filters);
    }, [filters, fetchInitialData]);

    const hayFiltros = Object.values(filters || {}).some((v) => v !== null && v !== undefined && v !== "");

    return (
        <>
            <DataTable
                columnas={columnas}
                filas={resulExamenList}
                filaKey="result_key"
                cargando={loading}
                error={error}
                onReintentar={() => fetchInitialData(currentPage, pageSize, filters)}
                etiquetaTabla="Resultados de exámenes"
                hayFiltrosAplicados={hayFiltros}
                textoVacioSinFiltros="Todavía no hay resultados de exámenes registrados."
                paginacion={{
                    page: currentPage,
                    pageSize,
                    total,
                    tamanos: [10, 25, 50, 100],
                    onCambio: (page, limit) => fetchInitialData(page, limit, filters),
                }}
            />

            <ModalResultadoExamen
                data={{
                    show: showUploadResultado,
                    handleClose: handleCloseUpload,
                    invoice: dataInvoice,
                }}
            />
        </>
    );
}

export default GridResultadoExamen;
